/**
 * External dependencies
 */
import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  Legend,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { size, get, slice } from "lodash";

/**
 * Internal dependencies
 */
import { COLORS, COMPARISON_COLORS } from "./constants";
import {
  toYAxisFormatter,
  formatResultSetToShortTitle,
  formatMaybeDateString,
  toNormalizedSeriesNames,
  toNormalizedResultData,
  isInFirstHalf,
} from "./utils";
import EmptyContent from "./base/empty-content";
import { CustomTooltip } from "../custom-tooltip";
import { CustomLegend } from "../custom-legend";

const AreaChartRenderer = ({
  resultSet,
  limit,
  colorTheme,
  points,
  stacked,
  dataFormatter,
  hideLegend = false,
  axisTickAngle = 0,
  compare,
}) => {
  let formatter = toYAxisFormatter(resultSet);

  let data = toNormalizedResultData(resultSet);

  const normalizedSeries = toNormalizedSeriesNames(resultSet);

  if (size(data) === 0) {
    return <EmptyContent />;
  }

  if (dataFormatter) {
    data = dataFormatter(data);
  }

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      className="chart__area-chart"
    >
      <AreaChart
        margin={{ top: 20, right: 40, bottom: 20, left: 20 }}
        data={data}
      >
        <CartesianGrid stroke="#eee" vertical={false} />
        <Tooltip dataKey="" content={CustomTooltip} formatter={formatter} />
        <YAxis
          tickLine={false}
          tickFormatter={formatter}
          axisLine={false}
          tick={{ fill: "#33475b", fontSize: 10 }}
        />
        {hideLegend ? null : (
          <Legend
            verticalAlign="top"
            align="left"
            iconType="circle"
            wrapperStyle={{ paddingBottom: 35 }}
            iconSize={10}
            content={(p) => <CustomLegend {...p} isCompare={compare} />}
          />
        )}
        <XAxis
          allowDataOverflow={false}
          stroke="#7c98b6"
          domain={[0, 0]}
          dataKey="x"
          tickLine={false}
          height={25}
          type="category"
          angle={axisTickAngle}
          tickFormatter={(v) => formatMaybeDateString(v)}
          tickMargin={10}
          tick={{ fill: "#33475b", fontSize: 10 }}
        />
        {normalizedSeries.map((series, i) => {
          const isFirstHalf = isInFirstHalf(normalizedSeries, i);

          const colors =
            compare === true
              ? isFirstHalf
                ? COLORS
                : COMPARISON_COLORS
              : COLORS;

          return (
            <Area
              key={series.key}
              type="linear"
              stackId={stacked && "a"}
              dataKey={series.key}
              stroke={get(colors, [colorTheme || "default", i])}
              name={formatResultSetToShortTitle(resultSet.queryType, series)}
              fill={get(colors, [colorTheme || "default", i])}
              fillOpacity={!compare || isFirstHalf ? 0.9 : 0.4}
              strokeOpacity={i == 0 ? 1 : 0.3}
              dot={
                points && {
                  stroke: colors[i % colors.length],
                  strokeWidth: 2,
                }
              }
            ></Area>
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AreaChartRenderer;
